import { Card } from '../App';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { ManaSymbol } from './ManaSymbol';
import { SmartAdviceBox } from './SmartAdviceBox';
import { calculateDeckPipCounts, generateDynamicManaAdvice } from '../lib/landAutoSuggest';
import { checkCardLegality } from '../lib/formatLegality';
import { useResolvedCardImage } from '../lib/scryfall';

interface PlanColors {
  [key: string]: { hex: string; bg: string; text: string; border: string };
}

interface DeckGridProps {
  groups: { name: string; cards: Card[] }[];
  onCardClick: (card: Card) => void;
  onUpdateCard: (cardId: string, updates: Partial<Card>) => void;
  onRemoveCard: (cardId: string) => void;
  onOpenPlanPopup?: (card: Card, plan: 'A' | 'B' | 'C' | 'D' | 'E') => void;
  isSideboard?: boolean;
  planColors?: PlanColors;
  format?: string;
  advice?: string[];
  onAutoSuggestLands?: () => void;
}

const defaultPlanColors: PlanColors = {
  A: { hex: '#22d3ee', bg: 'rgba(34,211,238,0.2)', text: '#22d3ee', border: 'rgba(34,211,238,0.3)' },
  B: { hex: '#34d399', bg: 'rgba(52,211,153,0.2)', text: '#34d399', border: 'rgba(52,211,153,0.3)' },
  C: { hex: '#fbbf24', bg: 'rgba(251,191,36,0.2)', text: '#fbbf24', border: 'rgba(251,191,36,0.3)' },
  D: { hex: '#a855f7', bg: 'rgba(168,85,247,0.2)', text: '#a855f7', border: 'rgba(168,85,247,0.3)' },
  E: { hex: '#fb7185', bg: 'rgba(251,113,133,0.2)', text: '#fb7185', border: 'rgba(251,113,133,0.3)' },
};

interface GridCardItemProps {
  card: Card;
  onCardClick: (card: Card) => void;
  onUpdateCard: (cardId: string, updates: Partial<Card>) => void;
  onRemoveCard: (cardId: string) => void;
  onOpenPlanPopup?: (card: Card, plan: 'A' | 'B' | 'C' | 'D' | 'E') => void;
  isSideboard: boolean;
  planColors: PlanColors;
  format: string;
  plans: readonly ['A', 'B', 'C', 'D', 'E'];
}

function GridCardItem({
  card,
  onCardClick,
  onUpdateCard,
  onRemoveCard,
  onOpenPlanPopup,
  isSideboard,
  planColors,
  format,
  plans,
}: GridCardItemProps) {
  const cardImgSrc = useResolvedCardImage(card.name, card.imageUrl);
  const baseMV = card.effectiveMV ?? card.cmc;
  const hasAnyPlanOverride = plans.some(plan => {
    const copyMVs = card.planCopyMVs?.[plan];
    return copyMVs && copyMVs.some(mv => mv !== baseMV);
  });

  // Scryfall format legality check
  const legality = checkCardLegality(card.legalities, format);
  const isIllegal = !legality.isLegal;

  return (
    <div
      key={card.id}
      className={`relative group rounded-sm overflow-hidden transition-all flex-shrink-0 
        w-[calc(25%-5px)] sm:w-[calc(16.66%-5px)] lg:w-[calc(12.5%-5px)]
        ${isIllegal
          ? 'ring-2 ring-red-500 shadow-[0_0_15px_rgba(239,68,68,0.8)] bg-red-950/20'
          : isSideboard
            ? 'bg-amber-500/10 hover:ring-amber-500/50'
            : 'bg-white/5 hover:ring-cyan-500/50'
        }`}
    >
      {/* Card thumbnail */}
      <div
        className="aspect-[5/7] relative cursor-pointer"
        onClick={() => onCardClick(card)}
      >
        <ImageWithFallback
          src={cardImgSrc}
          alt={card.name}
          className="w-full h-full object-cover"
        />
        {/* Quantity badge */}
        <div className={`absolute top-0.5 left-0.5 text-white text-[10px] px-1 rounded font-medium ${isSideboard ? 'bg-amber-600/90' : 'bg-black/80'}`}>
          {card.quantity}×
        </div>

        {/* Illegal badge */}
        {isIllegal && (
          <div className="absolute top-0.5 right-0.5 bg-red-600/90 text-white text-[7px] px-1 py-0.5 rounded font-bold uppercase tracking-wider shadow-md">
            {legality.status === 'banned' ? 'Banned' : 'Illegal'}
          </div>
        )}

        {/* MV Override indicator */}
        {hasAnyPlanOverride && !isIllegal && (
          <div className="absolute top-0.5 right-0.5 bg-purple-500/80 text-white text-[8px] px-1 rounded font-medium" title="Has plan MV overrides">
            ⚡
          </div>
        )}
        {/* Sideboard indicator */}
        {isSideboard && (
          <div className="absolute bottom-0.5 right-0.5 bg-amber-500/80 text-white text-[7px] px-0.5 rounded font-medium">
            SB
          </div>
        )}
      </div>

      {/* Hover controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/95 opacity-0 group-hover:opacity-100 transition-opacity p-1">
        {/* Card name */}
        <div className="text-[7px] text-white/80 truncate mb-0.5 px-0.5">{card.name}</div>

        {/* Qty controls row */}
        <div className="flex items-center justify-between gap-0.5 mb-1">
          <div className="flex items-center gap-0.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newQty = card.quantity - 1;
                if (newQty <= 0) {
                  onRemoveCard(card.id);
                } else {
                  onUpdateCard(card.id, { quantity: newQty });
                }
              }}
              className="w-4 h-4 bg-white/10 hover:bg-red-500/40 text-white/60 hover:text-white rounded text-[10px] flex items-center justify-center"
            >
              −
            </button>
            <span className="text-white text-[9px] w-3 text-center">{card.quantity}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpdateCard(card.id, { quantity: card.quantity + 1 });
              }}
              className="w-4 h-4 bg-white/10 hover:bg-cyan-500/40 text-white/60 hover:text-white rounded text-[10px] flex items-center justify-center"
            >
              +
            </button>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUpdateCard(card.id, { quantity: 4 });
            }}
            className="px-1 h-4 bg-cyan-500/30 text-cyan-300 hover:bg-cyan-500/50 rounded text-[8px] font-medium"
            title="Fill playset (4)"
          >
            ×4
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveCard(card.id);
            }}
            className="px-1 h-4 bg-red-500/30 text-red-300 hover:bg-red-500/50 rounded text-[8px]"
            title="Remove"
          >
            ×
          </button>
        </div>

        {/* Match buttons row (formerly Plan buttons) */}
        {onOpenPlanPopup && (
          <div className="flex items-center gap-0.5">
            {plans.map((plan, idx) => {
              const matchNum = idx + 1; // A=1, B=2, etc.
              const copyMVs = card.planCopyMVs?.[plan];
              const hasOverride = copyMVs && copyMVs.some(mv => mv !== baseMV);
              const colors = planColors[plan];

              return (
                <button
                  key={plan}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenPlanPopup(card, plan);
                  }}
                  className="flex-1 h-4 rounded text-[8px] font-medium transition-all"
                  style={{
                    backgroundColor: hasOverride ? colors.bg : `${colors.hex}15`,
                    color: hasOverride ? colors.text : `${colors.hex}80`,
                    boxShadow: hasOverride ? `inset 0 0 0 1px ${colors.border}` : 'none'
                  }}
                  title={hasOverride ? `Match ${matchNum}: Has overrides` : `Match ${matchNum}: Click to edit`}
                >
                  {matchNum}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function DeckGrid({
  groups,
  onCardClick,
  onUpdateCard,
  onRemoveCard,
  onOpenPlanPopup,
  isSideboard = false,
  planColors = defaultPlanColors,
  format = 'standard',
  advice,
  onAutoSuggestLands,
}: DeckGridProps) {
  const allCards = groups.flatMap(g => g.cards);
  const nonLandCards = allCards.filter(c => !c.isLand);
  const landCards = allCards.filter(c => c.isLand);
  const plans = ['A', 'B', 'C', 'D', 'E'] as const;
  const pipStats = calculateDeckPipCounts(allCards);
  const liveAdvice = (!isSideboard && advice && advice.length > 0)
    ? advice
    : !isSideboard ? generateDynamicManaAdvice(allCards, [], {}, {}, pipStats, 24, format) : [];

  const renderCard = (card: Card) => (
    <GridCardItem
      key={card.id}
      card={card}
      onCardClick={onCardClick}
      onUpdateCard={onUpdateCard}
      onRemoveCard={onRemoveCard}
      onOpenPlanPopup={onOpenPlanPopup}
      isSideboard={isSideboard}
      planColors={planColors}
      format={format}
      plans={plans}
    />
  );

  return (
    <div className="space-y-4">
      {/* 1. Non-Land Spells Grid */}
      {nonLandCards.length > 0 && (
        <div className={`flex flex-wrap gap-1.5 content-start ${isSideboard ? 'bg-amber-500/5 rounded-lg p-2 border border-amber-500/20' : ''}`}>
          {nonLandCards.map(renderCard)}
        </div>
      )}

      {/* 2. Pip Counter Meter Widget & Smart Advice Box (under non-land spells, above lands) */}
      {!isSideboard && (
        <div className="space-y-3 my-3">
          <div className="flex items-center justify-center gap-3 bg-slate-950/80 border border-purple-500/30 rounded-lg px-3 py-1.5 mx-auto w-fit text-xs font-medium">
            <span className="text-purple-400 font-semibold flex items-center gap-1">
              <span>Pip Meter:</span>
            </span>
            <div className="flex items-center gap-2">
              {(['W', 'U', 'B', 'R', 'G', 'C'] as const).map(c => {
                const count = pipStats.pips[c] || 0;
                if (count === 0 && c === 'C') return null;
                return (
                  <div key={c} className="flex items-center gap-1 bg-white/5 border border-white/10 px-1.5 py-0.5 rounded">
                    <ManaSymbol symbol={c} size="sm" />
                    <span className="text-white font-mono font-medium text-xs">{count}</span>
                  </div>
                );
              })}
            </div>

            {onAutoSuggestLands && (
              <button
                type="button"
                onClick={onAutoSuggestLands}
                className="px-2.5 py-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-medium rounded border border-cyan-400/50 shadow-md transition-all ml-2"
              >
                Suggest Lands
              </button>
            )}
          </div>

          {/* Smart Dynamic Mana Advice Box */}
          <SmartAdviceBox advice={liveAdvice} />
        </div>
      )}

      {/* 3. Lands Section Grid */}
      {landCards.length > 0 && (
        <div className="space-y-2">
          {!isSideboard && (
            <div className="text-xs text-white/50 font-medium tracking-wider uppercase">
              Lands ({landCards.reduce((acc, c) => acc + c.quantity, 0)})
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 content-start">
            {landCards.map(renderCard)}
          </div>
        </div>
      )}
    </div>
  );
}
